// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { AppointmentTypeDefinition } from './appointment-types';
import type { TriageStage } from './triage-stages';
import { getAllowedAppointmentTypeCodes } from './triage-stages';
import type { EligibilityStatus, SchedulingEligibility } from './types';

export interface SchedulingEligibilityInput {
  /** Insurance eligibility status */
  eligibilityStatus: EligibilityStatus;
  /** Patient's current triage stage (derived from treatment status) */
  triageStage: TriageStage | undefined;
  /** All available appointment type definitions from the CodeSystem */
  appointmentTypes: AppointmentTypeDefinition[];
  /** Appointment type codes the patient already has booked (prevents duplicates) */
  existingBookedTypeCodes?: string[];
  /**
   * When true, skip the `visibleInPortal` filter so the caller can see all
   * appointment types allowed for the triage stage — not just patient-self-
   * schedulable ones. Use in clinician-facing contexts (provider-app) where
   * the clinician books on behalf of the patient and is not constrained to
   * the patient portal catalog. Defaults to `false` (patient-app behavior).
   */
  includeNonPortalTypes?: boolean;
  /**
   * When true, bypass the triage-stage → appointment-type filter (Gate 3) so
   * the caller can pick any appointment type from the full catalog regardless
   * of the patient's current care stage. Use in clinician-facing contexts
   * where the provider exercises clinical judgment about which appointment
   * to book (e.g. booking a DME follow-up for a patient who hasn't reached
   * that stage yet). Insurance eligibility (Gate 1), triage-stage presence
   * (Gate 2), and duplicate-type prevention (Gate 4) still apply.
   * Defaults to `false` (patient-app behavior). An active RPM CarePlan also
   * permits an undefined triage stage for clinician-facing scheduling.
   */
  includeAllTriageStages?: boolean;
  /** Skip triage-stage presence checks when the caller has a separate scheduling rule. */
  ignoreTriageStage?: boolean;
  /** Clinician booking bypasses insurance and workflow-stage gates. */
  isClinicianBooking?: boolean;
  /**
   * When true, the patient pays out of pocket — bypass Gate 1 (insurance
   * eligibility). Gates 2 (triage stage), 3 (appointment type), and 4
   * (duplicate) still apply, subject to the RPM override. Defaults to false.
   */
  isSelfPay?: boolean;
  /**
   * When true, an active RPM CarePlan allows clinician-facing scheduling
   * regardless of the patient's current triage stage. Insurance eligibility
   * and duplicate appointment protections still apply.
   */
  rpmActive?: boolean;
  /** When true, keep already-booked types selectable for clinician booking. */
  allowDuplicateTypes?: boolean;
}

/**
 * Determines whether a patient can schedule an appointment and which types are available.
 *
 * Logic follows the scheduling flowchart:
 * 1. Insurance eligibility must be 'eligible'
 * 2. Triage stage must allow scheduling (not 'intake' or undefined), unless
 *    the caller identifies an active RPM CarePlan
 * 3. At least one appointment type must be available for the triage stage,
 *    unless a clinician/RPM override applies
 *
 * @param input - The patient's current status and available appointment types
 * @returns Scheduling eligibility result
 */
export function determineSchedulingEligibility(input: SchedulingEligibilityInput): SchedulingEligibility {
  const {
    eligibilityStatus,
    triageStage,
    appointmentTypes,
    existingBookedTypeCodes = [],
    includeNonPortalTypes = false,
    includeAllTriageStages = false,
    ignoreTriageStage = false,
    isClinicianBooking = false,
    isSelfPay = false,
    rpmActive = false,
    allowDuplicateTypes = false,
  } = input;

  const bypassPatientEligibility = isClinicianBooking;
  const allowAllAppointmentTypes = includeAllTriageStages || isClinicianBooking;
  const allowNonPortalTypes = includeNonPortalTypes || isClinicianBooking;

  // Gate 1: Insurance must be eligible — skipped entirely for self-pay patients.
  if (!isSelfPay && !bypassPatientEligibility) {
    if (eligibilityStatus === 'ineligible') {
      return {
        canSchedule: false,
        reason: 'Your insurance coverage is not active. Please contact your insurance provider or our support team.',
        allowedAppointmentTypes: [],
        triageStage,
      };
    }

    if (eligibilityStatus !== 'eligible') {
      return {
        canSchedule: false,
        reason: 'Your insurance eligibility must be confirmed before scheduling.',
        allowedAppointmentTypes: [],
        triageStage,
      };
    }
  }

  // Gate 2: Must have a triage stage
  if (!triageStage && !rpmActive && !bypassPatientEligibility && !ignoreTriageStage) {
    return {
      canSchedule: false,
      reason: 'Your care journey stage has not been determined yet.',
      allowedAppointmentTypes: [],
      triageStage,
    };
  }

  // Gate 3: Triage stage must have allowed appointment types.
  // Clinician-facing callers can bypass this via `includeAllTriageStages` —
  // they're not constrained to the catalog slice for the current care stage.
  const allowedCodes = getAllowedAppointmentTypeCodes(triageStage);
  if (!allowAllAppointmentTypes && !rpmActive && allowedCodes.length === 0) {
    return {
      canSchedule: false,
      reason: 'No appointments are available for your current care stage.',
      allowedAppointmentTypes: [],
      triageStage,
    };
  }

  // Filter appointment types. Two independent axes:
  //   - triage-stage gating: skipped when `includeAllTriageStages` (full catalog)
  //   - portal-visibility: skipped when `includeNonPortalTypes` (clinician override)
  // Both default to the patient-app behavior (gated + portal-only).
  const allowedTypes = appointmentTypes.filter(
    (type) =>
      (allowAllAppointmentTypes || rpmActive || allowedCodes.includes(type.coding.code ?? '')) &&
      type.schedulable !== false &&
      (allowNonPortalTypes || type.visibleInPortal)
  );

  if (allowedTypes.length === 0) {
    return {
      canSchedule: false,
      reason: 'No self-schedulable appointments are available for your current care stage.',
      allowedAppointmentTypes: [],
      triageStage,
    };
  }

  // Clinicians may deliberately schedule another appointment of the same type.
  if (allowDuplicateTypes) {
    const duplicateTypeCodes = allowedTypes
      .map((type) => type.coding.code ?? '')
      .filter((code) => existingBookedTypeCodes.includes(code));

    return { canSchedule: true, allowedAppointmentTypes: allowedTypes, triageStage, duplicateTypeCodes };
  }

  // Gate 4: Remove types the patient already has a booked appointment for
  const availableTypes = allowedTypes.filter((type) => !existingBookedTypeCodes.includes(type.coding.code ?? ''));

  if (availableTypes.length === 0) {
    return {
      canSchedule: false,
      reason:
        'You already have an appointment for your current care stage. Your care team will advance you to the next stage.',
      allowedAppointmentTypes: [],
      triageStage,
    };
  }

  return {
    canSchedule: true,
    allowedAppointmentTypes: availableTypes,
    triageStage,
  };
}
